import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Inbox, RefreshCw, ScrollText, ShieldAlert, ShieldCheck, ShieldX } from 'lucide-react';
import type { MailLogEntry, MailLogFilter } from '@dts/core';
import { api, errorMessage } from '@/lib/api';
import { useMe } from '@/lib/queries';
import { ago } from '@/lib/format';
import { Avatar } from './Avatar';
import { Button, Chip, cn, Empty, IconButton, Input } from './ui';

const FILTERS: { id: MailLogFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'inbox', label: 'Inbox' },
  { id: 'junk', label: 'Junk' },
  { id: 'unrouted', label: 'Unrouted' },
];

const SOURCES: Record<string, string> = {
  blocklist: 'Block list',
  safelist: 'Safe list',
  contact: 'In contacts',
  llm: 'AI model',
  admin: 'Admin',
  user: 'User',
  internal: 'Internal',
  none: 'Not checked',
  'llm-error': 'Model unavailable',
};

function sourceLabel(s?: string) {
  return s ? SOURCES[s] ?? s : 'Not checked';
}

/** Administrators: every incoming message, where it was filed and why. */
export function MailLogPanel() {
  const me = useMe().data;
  const qc = useQueryClient();
  const [filter, setFilter] = useState<MailLogFilter>('all');
  const [days, setDays] = useState(7);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const isAdmin = me?.user.role === 'admin';
  const log = useQuery({
    queryKey: ['mail-log', filter, days, search.trim()],
    queryFn: () => api.mailLog({ filter, days, search: search.trim() || undefined }),
    enabled: isAdmin,
    staleTime: 15_000,
  });

  if (me && !isAdmin) return <Empty icon={<ScrollText />} title="Administrators only" hint="Ask an administrator if you need to know why a message was filed as junk." />;

  const verdict = async (m: MailLogEntry, decision: 'junk' | 'not_junk', updateSenderList: boolean) => {
    setBusy(`${m.id}:${decision}:${updateSenderList}`);
    try {
      const r = await api.setMailLogVerdict(m.mailbox, m.id, decision, updateSenderList);
      toast.success(
        `Moved to ${r.folder}` +
          (r.senderList ? ` and ${m.fromAddress} added to ${m.mailboxName ?? 'their'}'s ${r.senderList === 'safe' ? 'safe' : 'block'} list` : ''),
      );
      qc.invalidateQueries({ queryKey: ['mail-log'] });
      qc.invalidateQueries({ queryKey: ['messages'] });
      qc.invalidateQueries({ queryKey: ['folders'] });
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const items = log.data?.items ?? [];
  return (
    <>
      <div className="mb-4 flex items-end justify-between gap-4">
        <div>
          <h2 className="text-[16px] font-semibold">Mail log</h2>
          <p className="mt-0.5 max-w-xl text-[12.5px] text-muted">Every incoming message across all mailboxes: where it was filed and why. Correct a verdict here and it moves in that person's mailbox too.</p>
        </div>
        <IconButton label="Refresh" onClick={() => log.refetch()}><RefreshCw className={cn(log.isFetching && 'animate-spin')} /></IconButton>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => <Chip key={f.id} active={filter === f.id} onClick={() => setFilter(f.id)}>{f.label}</Chip>)}
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="h-7 rounded-lg border border-line bg-raised px-2 text-[12.5px]">
          {[1, 7, 30, 90].map((d) => <option key={d} value={d}>{d === 1 ? 'Last 24 hours' : `Last ${d} days`}</option>)}
        </select>
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search sender, subject, recipient" className="h-8 min-w-[200px] flex-1 text-[12.5px]" />
      </div>

      {log.data && (
        <div className="mb-3 text-[12px] text-muted">
          {log.data.total} message{log.data.total === 1 ? '' : 's'}
          {log.data.total > items.length ? ` (showing the newest ${items.length})` : ''}
        </div>
      )}
      {log.isError && <div className="rounded-xl bg-danger/10 px-3 py-2 text-[12.5px] text-danger">{errorMessage(log.error)}</div>}
      {log.data && items.length === 0 && <div className="py-10 text-center text-[13px] text-muted">No incoming mail matches.</div>}

      <div className="space-y-2">
        {items.map((m) => {
          const junk = m.folder === 'Junk';
          const pct = m.junkProbability != null ? Math.round(m.junkProbability * 100) : null;
          return (
            <div key={`${m.mailbox}/${m.id}`} className="glass rounded-2xl px-4 py-3">
              <div className="flex items-start gap-3">
                <Avatar name={m.from || m.fromAddress} address={m.fromAddress} size={32} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-[13.5px] font-semibold">{m.subject || '(no subject)'}</span>
                    <span className="ml-auto shrink-0 text-[11.5px] text-faint" title={m.receivedUtc}>{m.receivedUtc ? ago(m.receivedUtc) : ''}</span>
                  </div>
                  <div className="truncate text-[12px] text-muted">
                    {m.from || m.fromAddress} → {m.recipient ?? 'unknown'}
                    {m.unrouted ? ' · no mailbox owns this address' : m.mailboxName ? ` · ${m.mailboxName}'s mailbox` : ''}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    {m.unrouted
                      ? <Chip color="#f59e0b"><ShieldAlert className="size-3" />Unrouted</Chip>
                      : junk
                        ? <Chip color="#f43f5e"><ShieldX className="size-3" />Junk</Chip>
                        : <Chip color="#10b981"><Inbox className="size-3" />{m.folder}</Chip>}
                    <Chip>{sourceLabel(m.junkSource)}</Chip>
                    {pct != null && (
                      <span className="inline-flex items-center gap-1.5 text-[11.5px] text-muted" title="Junk probability from the AI model">
                        <span className="h-1.5 w-16 overflow-hidden rounded-full bg-line">
                          <span className={cn('block h-full rounded-full', pct >= 70 ? 'bg-danger' : pct >= 40 ? 'bg-warn' : 'bg-ok')} style={{ width: `${pct}%` }} />
                        </span>
                        {pct}% junk
                      </span>
                    )}
                  </div>
                  {m.junkReason && <div className="selectable mt-1.5 text-[12px] text-muted">{m.junkReason}</div>}
                </div>
              </div>
              {!m.unrouted && (
                <div className="mt-2.5 flex flex-wrap justify-end gap-1.5">
                  {junk ? (
                    <>
                      <Button size="sm" variant="soft" loading={busy === `${m.id}:not_junk:false`} onClick={() => verdict(m, 'not_junk', false)}>Not junk</Button>
                      <Button size="sm" variant="soft" icon={<ShieldCheck className="size-3.5" />} loading={busy === `${m.id}:not_junk:true`} onClick={() => verdict(m, 'not_junk', true)}>Not junk &amp; always allow sender</Button>
                    </>
                  ) : (
                    <>
                      <Button size="sm" variant="ghost" loading={busy === `${m.id}:junk:false`} onClick={() => verdict(m, 'junk', false)}>Junk</Button>
                      <Button size="sm" variant="ghost" icon={<ShieldX className="size-3.5" />} loading={busy === `${m.id}:junk:true`} onClick={() => verdict(m, 'junk', true)}>Junk &amp; block sender</Button>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
